import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tire, Copy, ShieldCheck, PencilSimple, CircleNotch, UserCircle } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, getErrorMessage } from "@/lib/api";

const LABEL_CLS = "font-body text-[0.7rem] font-medium uppercase tracking-[0.12em] text-neutral-400";

const PARTS = [["inner_bearing", "Inner bearing"], ["outer_bearing", "Outer bearing"], ["seal", "Seal"]];

function PartNumbers({ hub, testid }) {
  const copy = async (v) => {
    try { await navigator.clipboard.writeText(v); toast.success(`Copied ${v}`); } catch { toast.error("Could not copy"); }
  };
  const parts = PARTS.filter(([k]) => hub[k]);
  if (!parts.length) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid={testid}>
      {parts.map(([k, label]) => (
        <button
          key={k}
          type="button"
          onClick={() => copy(hub[k])}
          title={`Copy ${label.toLowerCase()} number`}
          data-testid={`${testid}-${k}`}
          className="group text-left bg-black/40 border border-neutral-800 hover:border-[#73bf44]/50 rounded-lg px-3 py-2.5 transition-colors duration-200"
        >
          <div className="flex items-center justify-between text-[0.6rem] uppercase tracking-[0.14em] text-neutral-500">
            {label}
            <Copy size={12} className="opacity-40 group-hover:opacity-100 group-hover:text-[#73bf44] transition-opacity" />
          </div>
          <div className="font-mono text-sm text-white mt-1 break-all">{hub[k]}</div>
        </button>
      ))}
    </div>
  );
}

function HubPicker({ chassis, current, onSaved, onCancel }) {
  const [hubs, setHubs] = useState(null);
  const [pick, setPick] = useState(current || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/hub-types").then(({ data }) => setHubs(data)).catch(() => setHubs([]));
  }, []);

  if (hubs === null) {
    return <div className="flex items-center gap-2 text-sm text-neutral-500"><CircleNotch size={14} className="animate-spin" /> Loading hub types…</div>;
  }
  if (!hubs.length) {
    return <p className="text-sm text-neutral-500" data-testid="hub-none-available">Not recorded yet. Your dealer or the OTM team can confirm which hubs are fitted.</p>;
  }
  const chosen = hubs.find((h) => h.id === pick);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/caravans/${encodeURIComponent(chassis)}/hub`, { hub_type_id: pick });
      onSaved(data.hub);
      toast.success("Hubs saved to your caravan");
    } catch (e) {
      toast.error(getErrorMessage(e, "Couldn't save your hubs"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="hub-picker">
      <p className="text-sm text-neutral-400 leading-relaxed">
        Pick the hubs fitted to your van to see the matching bearing and seal numbers. We'll save it to your chassis for next time.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger data-testid="hub-select-trigger" className="h-11 flex-1 bg-black/40 border-neutral-700 text-white rounded-xl focus:ring-[#73bf44]">
            <SelectValue placeholder="Select your hubs…" />
          </SelectTrigger>
          <SelectContent className="bg-[#141414] border-neutral-800 text-neutral-100">
            {hubs.map((h) => <SelectItem key={h.id} value={h.id} data-testid="hub-option">{h.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={save}
          disabled={!pick || saving}
          data-testid="hub-save"
          className="h-11 px-5 rounded-full bg-[#73bf44] text-[#050505] text-sm font-bold hover:bg-[#5fa338] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Save hubs"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} data-testid="hub-cancel" className="h-11 px-4 text-sm text-neutral-400 hover:text-white transition-colors">Cancel</button>
        )}
      </div>
      {chosen && <PartNumbers hub={chosen} testid="hub-preview" />}
    </div>
  );
}

function SourceBadge({ customer }) {
  return customer ? (
    <span data-testid="hub-source-customer" className="inline-flex items-center gap-1 text-[0.6rem] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border border-neutral-700 text-neutral-300">
      <UserCircle size={12} weight="fill" /> Selected by you
    </span>
  ) : (
    <span data-testid="hub-source-admin" className="inline-flex items-center gap-1 text-[0.6rem] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border border-[#73bf44]/30 bg-[#73bf44]/10 text-[#73bf44]">
      <ShieldCheck size={12} weight="fill" /> Confirmed by OTM
    </span>
  );
}

export function HubField({ caravan, onChange }) {
  const [editing, setEditing] = useState(false);
  const hub = caravan.hub;
  const customer = hub?.source === "customer";
  const saved = (h) => { onChange({ ...caravan, hub: h, hub_type_id: h.id, hub_source: "customer" }); setEditing(false); };

  return (
    <div data-testid="spec-field-hub" className="bg-[#141414] border border-neutral-800 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Tire size={15} className="text-[#73bf44]" weight="duotone" />
        <span className={LABEL_CLS}>Hubs</span>
        {hub && !editing && <SourceBadge customer={customer} />}
        {hub && customer && !editing && (
          <button type="button" onClick={() => setEditing(true)} data-testid="hub-change" className="ml-auto inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#73bf44] transition-colors">
            <PencilSimple size={12} /> Change
          </button>
        )}
      </div>
      {hub && !editing ? (
        <div className="space-y-3">
          <div className="font-body text-sm font-semibold text-white" data-testid="hub-name">{hub.name}</div>
          <PartNumbers hub={hub} testid="hub-parts" />
          <p className="text-xs text-neutral-500">Tap a number to copy it. Always check it against the numbers on your old bearings and seals before ordering.</p>
        </div>
      ) : (
        <HubPicker chassis={caravan.chassis_number} current={hub?.id} onSaved={saved} onCancel={hub ? () => setEditing(false) : null} />
      )}
    </div>
  );
}
